import React, { useEffect, useState } from 'react'
import { Col, Input, Row, FormGroup, Alert } from 'reactstrap'
import { Controller, ErrorMessage, useFormContext } from 'react-hook-form'
import Select from 'react-select'
import Switch from 'react-switch'
import { grainTypeOptions } from './constants'
import { useFormUtils } from 'views/shared/useFormUtils'
import PropTypes from 'prop-types'
import { isNotAlpha, isNotNumericDecimal } from 'context/Utilities'

const BSInfantCerealPackageReview = ({ item, isDisabled }) => {
  const [packaging, setPackaging] = useState({
    fkCategory: item.fkCategory,
    fkSubCategory: item.fkSubCategory,
    GrainType: item.GrainType,
    OtherGrainType: item.OtherGrainType,
    Iron: item.Iron,
    IronCertification: item.IronCertification,
    NoAdditionalIngredients: item.NoAdditionalIngredients,
  })
  const methods = useFormContext()
  const {
    setSelectReviewer,
    setSwitchReviewer,
    onChangeReviewer,
    asterisk,
  } = useFormUtils()
  useEffect(() => {
    if (item !== null) {
      const {
        GrainType,
        OtherGrainType,
        Iron,
        IronCertification,
        NoAdditionalIngredients,
      } = item
      setPackaging({
        GrainType,
        OtherGrainType,
        Iron,
        IronCertification,
        NoAdditionalIngredients,
      })
    }
    // eslint-disable-next-line
    }, [item])

  return (
    <>
      <Row>
        <Col xs={4}>
          <p>
            <strong>Grain Type {asterisk}</strong>
          </p>
          <Controller
            as={Select}
            className="react-select primary"
            classNamePrefix="react-select"
            options={grainTypeOptions}
            defaultValue={{
              label: packaging.GrainType,
              value: packaging.GrainType,
            }}
            name="GrainType"
            control={methods.control}
            rules={{ required: true }}
            placeholder={'Select Grain Type'}
            onChange={([selected, value]) => {
              setSelectReviewer(item, selected, value)
              setPackaging({ ...packaging, GrainType: selected.value })
              return selected
            }}
            isDisabled={isDisabled}
          />
          <p className={'text-danger'}>
            <ErrorMessage
              errors={methods.errors}
              name="GrainType"
              message={'⚠ This selection is required.'}
              as={<Alert color={'danger'} className={'mt-3'} />}
            />
          </p>
          <br />
          <br />
          <br />
          {packaging.GrainType === 'Other' && (
            <FormGroup>
              <p>
                <strong>Other Type (Please Specify) {asterisk}</strong>
              </p>
              <Controller
                as={Input}
                name="OtherGrainType"
                placeholder={'Other Type'}
                defaultValue={packaging.OtherGrainType}
                onKeyDown={e => {
                  if (isNotAlpha(e.key) && e.key !== '-' && e.key !== ' ') {
                    e.preventDefault()
                  }
                }}
                control={methods.control}
                rules={{
                  maxLength: {
                    value: 30,
                    message: '⚠ Other Type exceeds 30 characters.',
                  },
                  required: {
                    value: true,
                    message: '⚠ Other Type is required.',
                  },
                }}
                onChange={([event]) => {
                  onChangeReviewer(item, event)
                  return event
                }}
                disabled={isDisabled}
              />
              <p className={'text-danger'}>
                <ErrorMessage
                  errors={methods.errors}
                  name="OtherGrainType"
                  as={<Alert color={'danger'} className={'mt-3'} />}
                />
              </p>
            </FormGroup>
          )}
        </Col>
        <Col xs={4}>
          <FormGroup>
            <p>
              <strong>Iron (mg) per 100g dry cereal {asterisk}</strong>
            </p>
            <Controller
              as={Input}
              name="Iron"
              type="number"
              step="0.01"
              placeholder={'Min 45mg iron per 100g dry cereal'}
              defaultValue={packaging.Iron}
              onKeyDown={e => {
                if (isNotNumericDecimal(e.key)) {
                  e.preventDefault()
                }
              }}
              control={methods.control}
              rules={{
                maxLength: {
                  value: 6,
                  message:
                    '⚠ Iron (mg) per 100g dry cereal exceeds 6 characters.',
                },
                min: {
                  value: 45,
                  message:
                    '⚠ Iron (mg) per 100g dry cereal is below the 45mg minimum allowed.',
                },
                required: {
                  value: true,
                  message: '⚠ Please enter Iron (mg) per 100g dry cereal.',
                },
                pattern: {
                  value: /^\d+(\.\d{1,2})?$/,
                  message: '⚠ Maximum two decimal places allowed.',
                },
              }}
              onChange={([event]) => {
                onChangeReviewer(item, event)
                return event
              }}
              disabled={isDisabled}
            />
            <p className={'text-danger'}>
              <ErrorMessage
                errors={methods.errors}
                name="Iron"
                as={<Alert color={'danger'} className={'mt-3'} />}
              />
            </p>
          </FormGroup>
        </Col>
        <Col xs={4}>
          <FormGroup>
            <p>
              <strong>
                I certify that this product contains a minimum of 45mg of iron
                per 100 grams of dry cereal. {asterisk}
              </strong>
            </p>
            <Controller
              as={Switch}
              name="IronCertification"
              id="IronCertification"
              defaultValue={packaging.IronCertification}
              onChange={([checked, event, id]) => {
                setSwitchReviewer(item, checked, event, id)
                return checked
              }}
              control={methods.control}
              rules={{
                validate: IronCertification => IronCertification === true,
              }}
              disabled={isDisabled}
            />
            <br />
            <p className={'text-danger'}>
              <ErrorMessage
                errors={methods.errors}
                name="IronCertification"
                message={
                  '⚠ You must certify that this product contains a minimum of 45mg of iron per 100 grams of dry cereal.'
                }
                as={<Alert color={'danger'} className={'mt-3'} />}
              />
            </p>
          </FormGroup>
          <br />
          <hr />
          <FormGroup>
            <p>
              <strong>
                I certify that this product does not contain added ingredients,
                such as formula, milk, fruits, vegetables, sugars, sweeteners,
                probiotics, or DHA. {asterisk}
              </strong>
            </p>
            <Controller
              as={Switch}
              name="NoAdditionalIngredients"
              id="NoAdditionalIngredients"
              defaultValue={packaging.NoAdditionalIngredients}
              onChange={([checked, event, id]) => {
                setSwitchReviewer(item, checked, event, id)
                return checked
              }}
              control={methods.control}
              rules={{
                validate: NoAdditionalIngredients =>
                  NoAdditionalIngredients === true,
              }}
              disabled={isDisabled}
            />
            <br />
            <p className={'text-danger'}>
              <ErrorMessage
                errors={methods.errors}
                name="NoAdditionalIngredients"
                message={
                  '⚠ You must certify that this product does not contain added ingredients, such as formula, milk, fruits, vegetables, sugars, sweeteners, probiotics, or DHA.'
                }
                as={<Alert color={'danger'} className={'mt-3'} />}
              />
            </p>
          </FormGroup>
        </Col>
      </Row>
    </>
  )
}

BSInfantCerealPackageReview.propTypes = {
  item: PropTypes.object,
  isDisabled: PropTypes.bool,
}

export default BSInfantCerealPackageReview
